import { CoquilleProduit } from "@dust-tt/front/components/compta/coquille/CoquilleProduit";
import { EcranInterrompu } from "@dust-tt/front/components/compta/coquille/EcranInterrompu";
import {
  ROUTE_CONNEXION,
  ROUTE_INSTALLATION,
  ROUTE_REINITIALISATION,
} from "@dust-tt/front/lib/compta/connexion/porte";
import { UnauthenticatedPage } from "@spa/app/layouts/UnauthenticatedPage";
import { withSuspense } from "@spa/app/routes/withSuspense";
import type { RouteObject } from "react-router-dom";

// Les routes PRODUIT du cabinet comptable, par opposition à `laboRoutes`, qui
// sert les maquettes sur fixtures.
//
// Pourquoi un fichier à part et pas `comptaRoutes.tsx` : ce dernier appartient
// aux trois assistants depuis le 01/10 (frontière de propriété, AGENTS.md
// racine). Les deux produits partagent la SPA ; ils ne partagent pas leurs
// routes.
//
// Un écran rejoint ce fichier UN PAR UN, le jour où il est branché sur de
// vraies données, et il quitte `laboRoutes.tsx` le même jour : promouvoir un
// écran qui affiche encore des fixtures ferait passer des données de
// démonstration pour les chiffres du cabinet, et un écran branché monté au labo
// plante, puisqu'il lit son dossier dans une route qui n'en porte pas.

const DossierApercuPage = withSuspense(
  () => import("@dust-tt/front/components/pages/compta/DossierApercuPage"),
  "DossierApercuPage"
);
const DossierFichiersPage = withSuspense(
  () => import("@dust-tt/front/components/pages/compta/DossierFichiersPage"),
  "DossierFichiersPage"
);
const DossierRevuePage = withSuspense(
  () => import("@dust-tt/front/components/pages/compta/DossierRevuePage"),
  "DossierRevuePage"
);
const DossierTvaPage = withSuspense(
  () => import("@dust-tt/front/components/pages/compta/DossierTvaPage"),
  "DossierTvaPage"
);
const DossierCahierPage = withSuspense(
  () => import("@dust-tt/front/components/pages/compta/DossierCahierPage"),
  "DossierCahierPage"
);
const PortefeuilleProceduresPage = withSuspense(
  () =>
    import("@dust-tt/front/components/pages/compta/PortefeuilleProceduresPage"),
  "PortefeuilleProceduresPage"
);
const PortefeuilleDossiersPage = withSuspense(
  () =>
    import("@dust-tt/front/components/pages/compta/PortefeuilleDossiersPage"),
  "PortefeuilleDossiersPage"
);
const ClientsPage = withSuspense(
  () => import("@dust-tt/front/components/pages/compta/ClientsPage"),
  "ClientsPage"
);
const ParametresPage = withSuspense(
  () => import("@dust-tt/front/components/pages/compta/ParametresPage"),
  "ParametresPage"
);
const RelancesPage = withSuspense(
  () => import("@dust-tt/front/components/pages/compta/RelancesPage"),
  "RelancesPage"
);
const EcheancierPage = withSuspense(
  () => import("@dust-tt/front/components/pages/compta/EcheancierPage"),
  "EcheancierPage"
);
const PortefeuilleFichiersPage = withSuspense(
  () =>
    import("@dust-tt/front/components/pages/compta/PortefeuilleFichiersPage"),
  "PortefeuilleFichiersPage"
);
const PortefeuilleTravauxPage = withSuspense(
  () =>
    import("@dust-tt/front/components/pages/compta/PortefeuilleTravauxPage"),
  "PortefeuilleTravauxPage"
);
const TableauxDeBordPage = withSuspense(
  () => import("@dust-tt/front/components/pages/compta/TableauxDeBordPage"),
  "TableauxDeBordPage"
);
const OnboardingAdminPage = withSuspense(
  () => import("@dust-tt/front/components/pages/compta/OnboardingAdminPage"),
  "OnboardingAdminPage"
);
const DossierCloturePage = withSuspense(
  () => import("@dust-tt/front/components/pages/compta/DossierCloturePage"),
  "DossierCloturePage"
);
const ConnexionPage = withSuspense(
  () => import("@dust-tt/front/components/pages/compta/ConnexionPage"),
  "ConnexionPage"
);
const ReinitialiserPage = withSuspense(
  () => import("@dust-tt/front/components/pages/compta/ReinitialiserPage"),
  "ReinitialiserPage"
);
const InstallationCabinetPage = withSuspense(
  () =>
    import("@dust-tt/front/components/pages/compta/InstallationCabinetPage"),
  "InstallationCabinetPage"
);

// Sans session, par construction : c'est l'écran qu'on voit justement parce
// qu'on n'en a pas.
export const cabinetUnauthenticatedRoutes: RouteObject[] = [
  {
    element: <UnauthenticatedPage />,
    children: [
      { path: ROUTE_CONNEXION, element: <ConnexionPage /> },
      // Série 2, lot C2: the public installation of a new cabinet.
      { path: ROUTE_INSTALLATION, element: <InstallationCabinetPage /> },
      // Recette de prod du 13/10 : le lien « mot de passe oublié » menait ici,
      // et rien n y était monté.
      { path: ROUTE_REINITIALISATION, element: <ReinitialiserPage /> },
    ],
  },
];

// Les écrans branchés du cabinet, sous /w/:wId, dans LEUR coquille.
//
// `CoquilleProduit` est le parent de tous : fournisseur de coquille, toasts et
// layout, exactement ce que le labo pose autour de ses maquettes. Jusqu'au
// 11/10 ce parent n'existait pas, et la Revue puis le Cahier plantaient à leur
// adresse produit sur `useConfigurerCoquille` (voir `CoquilleProduit.tsx`).
//
// Deux portées :
//  - le cabinet (`dossiers`, `clients`, `relances`, `parametres`) : les écrans qui lisent
//    ce qui vaut pour tous les dossiers du cabinet ;
//  - le dossier (`spaces/:spaceId/...`) : l'adresse copie celle de l'API, et
//    c'est de cette route que l'écran tire son dossier, par
//    `useRequiredPathParam("spaceId")` comme chez Dust.
//
// Hors de `AppContentRouterLayout` : la compta a sa propre coquille, elle
// n'emprunte pas la navigation de Dust.
export const cabinetDossierRoutes: RouteObject[] = [
  {
    element: <CoquilleProduit />,
    // Si la coquille elle-même tombe, il n'y a plus de creux où loger l'erreur.
    errorElement: <EcranInterrompu pleinEcran />,
    children: [
      { path: "dossiers", element: <PortefeuilleDossiersPage /> },
      { path: "procedures", element: <PortefeuilleProceduresPage /> },
      { path: "travaux", element: <PortefeuilleTravauxPage /> },
      { path: "fichiers", element: <PortefeuilleFichiersPage /> },
      { path: "clients", element: <ClientsPage /> },
      { path: "relances", element: <RelancesPage /> },
      { path: "echeancier", element: <EcheancierPage /> },
      { path: "tableaux-de-bord", element: <TableauxDeBordPage /> },
      { path: "parametres", element: <ParametresPage /> },
      { path: "spaces/:spaceId/apercu", element: <DossierApercuPage /> },
      { path: "spaces/:spaceId/revue", element: <DossierRevuePage /> },
      { path: "spaces/:spaceId/fichiers", element: <DossierFichiersPage /> },
      { path: "spaces/:spaceId/cahier", element: <DossierCahierPage /> },
      { path: "spaces/:spaceId/cloture", element: <DossierCloturePage /> },
      { path: "spaces/:spaceId/tva", element: <DossierTvaPage /> },
    ].map((route) => ({ ...route, errorElement: <EcranInterrompu /> })),
  },
  // The first steps of a new cabinet (série 2, lots C3+C4): full page, like
  // the entry screens, so OUTSIDE the coquille.
  {
    path: "premiers-pas",
    element: <OnboardingAdminPage />,
    errorElement: <EcranInterrompu pleinEcran />,
  },
];
